import { Divider, Flex, Form, Input, Space, Typography, message } from "antd";
import { useState } from "react";
import AuthService from "../../Services/auth.service";
import Button from "../Atoms/DynamicButton";

interface SignInFormProps {
  onSuccess?: () => void;
  onForgotPassword?: () => void;
  onSignUpClick?: () => void;
  showSignUpLink?: boolean;
  showForgotPasswordLink?: boolean;
  redirectUrl?: string | null;
}

const SignInForm: React.FC<SignInFormProps> = ({
  onSuccess,
  onForgotPassword,
  onSignUpClick,
  showSignUpLink = true,
  showForgotPasswordLink = true,
  redirectUrl = null,
}) => {
  const [isLoading, setLoading] = useState(false);
  const authService = AuthService.getInstance();

  const handleSignIn = async (formValues: any) => {
    try {
      setLoading(true);
      const response = await authService.signIn(formValues);
      if (response) {
        if (response.status === 500) {
          message.error("No account found with this email. Please sign up or check your email.");
          return;
        } else if (response.status === 401) {
          message.error(`${response?.data?.message || "Unauthorized"}, Please check your credentials`);
          return;
        } else if (response.status === 403) {
          message.error("Please check your credentials");
          return;
        } else if (response.status === 400) {
          if (response?.data?.message === "Complete the account setup") {
            message.warning("Account Setup Required, Please complete your account setup. Check your email for instructions.");
            return;
          } else {
            message.error(`${response?.data?.message || "Please check your input and try again"}`);
            return;
          }
        }

        if (response?.data?.access_token) {
          message.success("You've been signed in successfully.");
          onSuccess?.();
        } else {
          console.error("Invalid response from server");
        }
      }
    } catch (err) {
      console.error("Error while signing in user:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuthClick = async () => {
    try {
      const domain = window.location.hostname;
      await authService.handleGoogleAuth(domain, redirectUrl ?? undefined);
    } catch (error) {
      console.error("Unable to sign in with Google:", error);
      message.error("Unable to create your account. Please try again.");
    }
  };

  const handleLinkedinAuthClick = async () => {
    try {
      const domain = window.location.hostname;
      await authService.handleLinkedinAuth(domain, redirectUrl ?? undefined);
    } catch (error) {
      console.error("Unable to sign in with LinkedIn:", error);
      message.error("Unable to create your account. Please try again.");
    }
  };

  return (
    <Form
      name="signin_form"
      className="auth-form"
      initialValues={{ remember: true }}
      onFinish={handleSignIn}
      autoComplete="off"
    >
      <Form.Item
        name="email"
        rules={[
          {
            required: true,
            pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
            message: "Please enter a valid email address!",
          },
          {
            validator: (_, value) => {
              if (value && !value.includes("@")) {
                return Promise.reject(new Error("Email must contain @ symbol"));
              }
              if (value && value.length > 0 && value.length < 5) {
                return Promise.reject(new Error("Email is too short"));
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <Input placeholder="Email" />
      </Form.Item>
      <Form.Item
        name="password"
        rules={[
          {
            required: true,
            message: "Please enter your password!",
          },
        ]}
      >
        <Input.Password placeholder="Password" />
      </Form.Item>

      <Form.Item>
        <Space size={16} direction="vertical" style={{ width: "100%" }}>
          {showForgotPasswordLink && (
            <Flex align="center" justify="end" style={{ marginBottom: 8 }}>
              <Button
                onClick={onForgotPassword}
                type="link"
              >
                Forgot password?
              </Button>
            </Flex>
          )}
          <Button type="primary" htmlType="submit" block loading={isLoading}>
            {isLoading ? "" : "Sign In"}
          </Button>
          <Divider orientation="center">or</Divider>
          <Flex gap={10} vertical={true}>
            <Button
              icon={
                <img
                  src="/icons/google.svg"
                  alt="Google"
                  style={{ width: 20 }}
                />
              }
              onClick={handleGoogleAuthClick}
              block
            >
              Sign in with Google
            </Button>
            <Button
              icon={
                <img
                  src="/icons/linkedin.svg"
                  alt="LinkedIn"
                  style={{ width: 20 }}
                />
              }
              onClick={handleLinkedinAuthClick}
              block
            >
              Sign in with LinkedIn
            </Button>
          </Flex>
          {showSignUpLink && (
            <Typography.Paragraph style={{ textAlign: "center", margin: 0 }}>
              Don't have an account?
              <Button onClick={onSignUpClick} type="link">
                Create an account
              </Button>
            </Typography.Paragraph>
          )}
        </Space>
      </Form.Item>
    </Form>
  );
};

export default SignInForm;
